import * as React from 'react';
import classNames from 'classnames';
import * as UI from 'core/ui';
import classnames from 'classnames';

import { PaymentMethodDTO, Res } from 'core/api';
import { ActionButtonDefinition, Keypad, PosScreenClassComponent, ScreenSize, ThemeManager } from 'pos/ui';
import * as Payment from './PrepaymentStatus';
import * as Images from 'pos/Images';
import { Cols, Col, Flex } from 'core/ui';
import { Modal } from 'core/ui-next/modal';

type Props = {
	maxAvailableChange: number;
	pendingAmount: number,
	paidAmount: number,
	method: PaymentMethodDTO,
	imageSrc: string | SvgIcon,

} & Modal.Actions<Payment.Status>;


export default class CreateAdvancedPaymentScreen extends PosScreenClassComponent<Props, Payment.Status> {

	protected txtPaidAmount = React.createRef<UI.PriceBox<Payment.Status>>();
	protected txtTipAmount = React.createRef<UI.PriceBox<Payment.Status>>();
	protected form = React.createRef<UI.Form<Payment.Status>>();

	protected getScreenId(): string {
		return 'CREATE_ADVANCED_PAYMENT';
	}

	protected getTitle(): string {
		return this.props.method.Name;
	}

	protected getImage(): string | SvgIcon {
		return this.props.imageSrc || Images.AddPayment;
	}

	protected getSize(): ScreenSize {
		return { width: 375, height: 700 };
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			{ title: Res.Web.CREATE_ADVANCED_PAY_BTN(), image: this.props.imageSrc || Images.AddPayment, action: this.processPayment }
		];
	}

	protected useKeypad(): boolean {
		return false;
	}

	processPayment = async (): Promise<void> => {

		const ok = await this.form.current?.updateModel();
		if (!ok)
			return;

		const canPay = await Payment.canPay(this.state);
		if (canPay)
			this.close(this.state);
	};

	completeTip = async () => {
		await this.enqueue(async () => {
			const newStatus = await Payment.completeTipAmount({ ...this.state, TipAmount: this.getTipAmount() });
			await this.setStateAsync(newStatus, async () => await this.form.current?.remountAll());
		});
	};

	updateChange = async () => {

		await this.enqueue(async () => {
			const amountToPay = this.getPaidAmount() < this.props.pendingAmount ? this.getPaidAmount() : this.props.pendingAmount;
			const newStatus = await Payment.updateChangeAmount({ ...this.state }, amountToPay, this.getPaidAmount());
			await this.setStateAsync(newStatus, async () => await this.form.current?.remount('ChangeAmount'));
		});
	};

	getPaidAmount = (): number => {
		const paidAmountResult = this.txtPaidAmount.current?.getValue();
		return paidAmountResult?.ok ? paidAmountResult.value : this.state.PaidAmount;
	};

	getTipAmount = (): number => {
		const tipAmountResult = this.txtTipAmount.current?.getValue();
		return tipAmountResult?.ok ? tipAmountResult.value : this.state.TipAmount;
	};

	buildForm = () => {

		const { Form, PriceBox } = UI.controlsFor<Payment.Status>();

		// No puedo usar control-label porque en pantallas pequeñas bootstrap asume que la etiqueta va
		// encima del control y no mete el padding para alinearla
		const labelStyle = { paddingTop: 7 };

		// Ajusto algunos margins para que en el Castles S1F2 no haya que hacer scroll. Aquí sería genial
		// tener el sistema de breakpoints de tailwind, pero es lo que hay. De todas formas, aun con estas
		// clases, si hay algún mensaje de error será necesario hacer scroll, pero al menos la vista inicial
		// es más cómoda.
		const pendingClassNames = classNames({
			'text-26 text-center font-bold': true,
			'm-b': ThemeManager.isLargeDevice()
		});

		const formClassNames = classNames({
			'p-l-xl p-r-xl': true,
			'm-t-md m-b-md': ThemeManager.isLargeDevice()
		});

		return <Form model={this.state} ref={this.form} className={formClassNames}>
			<Cols className={pendingClassNames}>
				<Col width='*'>{Res.Web.CREATE_ADVANCED_PAYMENT_DATA_PENDING_AMOUNT_LBL(this.props.pendingAmount)}</Col>
			</Cols>

			<Cols>
				<Col style={labelStyle} width='*'>{Res.Web.CREATE_ADVANCED_PAYMENT_DATA_PAID_AMOUNT_LBL()}</Col>
				<Col width='*'>
					<PriceBox
						ref={this.txtPaidAmount}
						linkTo={x => x.PaidAmount}
						allowNegative={false}
						onValueChange={this.updateChange}
						className='text-right'
						rules={[UI.Constraints.maxDecimalPlaces(2), UI.Constraints.maxLength(10), UI.Constraints.greaterThanZero]}
						required>
					</PriceBox>
				</Col>
			</Cols>

			{this.state.Method.RegisterTip
				? <Cols>
					<Col style={labelStyle} width='*'>{Res.Web.CREATE_ADVANCED_PAYMENT_DATA_TIP_AMOUNT_LBL()}</Col>
					<Col width='*'>
						<PriceBox
							ref={this.txtTipAmount}
							linkTo={x => x.TipAmount}
							allowNegative={false}
							className='text-right'
							childrenPosition='prepend'
							rules={[UI.Constraints.maxDecimalPlaces(2), UI.Constraints.maxLength(10)]}
							required>
							<UI.TextBoxButton onClick={this.completeTip} ><i className='fa fa-arrow-right text-info' /></UI.TextBoxButton>
						</PriceBox>
					</Col>
				</Cols>
				: null
			}

			<Cols>
				<Col style={labelStyle} width='*'>{Res.Web.CREATE_ADVANCED_PAYMENT_DATA_CHANGE_AMOUNT_LBL()}</Col>
				<Col style={labelStyle} width='*' className='text-right'>
					{UI.Format.asPrice(this.state.ChangeAmount)}
				</Col>
			</Cols>

			<p className={classnames({ 'has-error': this.state.ErrorMessage, 'text-center': true, 'p-t-md': true })}>
				<UI.ErrorBlock msg={this.state.ErrorMessage} />
			</p>
		</Form>;
	};

	createInitialState(props: Props): Nullable<Payment.Status> {
		return {
			AmountToPay: props.pendingAmount,
			PaidAmount: props.paidAmount,
			PendingAmount: props.pendingAmount,
			ChangeAmount: Math.max(props.paidAmount - props.pendingAmount, 0),
			TipAmount: 0,
			Method: props.method,
			ThereIsEnoughChange: true,
			ErrorMessage: ''
		};
	}

	onComponentDidMount(): void { }

	onComponentWillUnmount(): void { }

	getContent(): React.ReactNode {
		return <Flex.Vertical>
			<Flex.Item size='*'>
				{this.buildForm()}
			</Flex.Item>
			<Flex.Item>
				<Keypad isFullScreen={false} isLargeDevice={ThemeManager.isLargeDevice()} alwaysVisible />
			</Flex.Item>
		</Flex.Vertical>;
	}
}

